import { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import LoadingSpinner from './components/common/LoadingSpinner';
import ScrollToTop from './components/common/ScrollToTop';

// Lazy load pages with webpackChunkName for better caching
const Landing = lazy(() => import(/* webpackChunkName: "landing" */ './pages/Landing'));
const StoreFront = lazy(() => import(/* webpackChunkName: "storefront" */ './pages/store/StoreFront'));
const ProductDetail = lazy(() => import(/* webpackChunkName: "storefront" */ './pages/store/ProductDetail'));
const ReturnPolicy = lazy(() => import(/* webpackChunkName: "storefront" */ './pages/store/ReturnPolicy'));
const SuperAdminLayout = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/Layout'));
const SuperAdminDashboard = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/Dashboard'));
const SuperAdminStores = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/Stores'));
const SuperAdminCreateStore = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/CreateStore'));
const SuperAdminPlans = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/Plans'));
const SuperAdminPayments = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/Payments'));
const SuperAdminAnnouncements = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/Announcements'));
const SuperAdminActivityLogs = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/ActivityLogs'));
const SuperAdminRevenueDashboard = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/RevenueDashboard'));
const SuperAdminStoreHealth = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/StoreHealth'));
const SuperAdminInvoices = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/Invoices'));
const SuperAdminSupportTickets = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/SupportTickets'));
const SuperAdminAnalytics = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/Analytics'));
const SuperAdminStoreRevenue = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/StoreRevenue'));
const AdminLayout = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Layout'));
const AdminDashboard = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Dashboard'));
const AdminProducts = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Products'));
const AdminOrders = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Orders'));
const AdminCustomers = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Customers'));
const AdminSettings = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Settings'));
const AdminAnalytics = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Analytics'));
const AdminContent = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Content'));
const AdminCoupons = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Coupons'));
const AdminCategories = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Categories'));
// const AdminAutoReply = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AutoReply')); // Disabled for now
const AdminAffiliates = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Affiliates'));
const AdminSupport = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/Support'));
const AdminSubscriptionPayment = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/SubscriptionPayment'));
const SetupWizard = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/SetupWizard'));
const ProductForm = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/ProductForm'));
const SuperAdminSubscriptionDashboard = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/SubscriptionDashboard'));
const SuperAdminPaymentManagement = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/PaymentManagement'));
const SuperAdminCommissions = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/Commissions'));
const SuperAdminProfile = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/Profile'));
const SuperAdminSecuritySettings = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/SecuritySettings'));
const SuperAdminTermsAndConditions = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/TermsAndConditions'));
const SuperAdminPrivacyPolicy = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/PrivacyPolicy'));
const AdsManager = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/AdsManager'));
const CreateAd = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/CreateAd'));
const EditAd = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/EditAd'));
const AdAnalytics = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/super-admin/AdAnalytics'));
const MerchantApplicationsPage = lazy(() => import(/* webpackChunkName: "super-admin" */ './pages/admin/MerchantApplicationsPage'));
const TermsAndConditions = lazy(() => import(/* webpackChunkName: "legal" */ './pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "legal" */ './pages/PrivacyPolicy'));
const Login = lazy(() => import(/* webpackChunkName: "auth" */ './pages/auth/Login'));
const Register = lazy(() => import(/* webpackChunkName: "auth" */ './pages/auth/Register'));
const MerchantVerification = lazy(() => import(/* webpackChunkName: "auth" */ './pages/MerchantVerification'));
const Blog = lazy(() => import(/* webpackChunkName: "blog" */ './pages/Blog'));
const BlogPost = lazy(() => import(/* webpackChunkName: "blog" */ './pages/BlogPost'));


function App() {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
        {/* Public Store Routes */}
        <Route path="/store/:slug" element={<StoreFront />} />
        <Route path="/store/:slug/product/:productId" element={<ProductDetail />} />
        <Route path="/store/:slug/return-policy" element={<ReturnPolicy />} />
        
        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/merchant-verification" element={<MerchantVerification />} />
        
        {/* Public Legal Routes */}
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        {/* Blog Routes — SEO Content Marketing */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        
        {/* Super Admin Routes */}
        <Route path="/super-admin" element={<SuperAdminLayout />}>
          <Route index element={<SuperAdminDashboard />} />
          <Route path="stores" element={<SuperAdminStores />} />
          <Route path="stores/create" element={<SuperAdminCreateStore />} />
          <Route path="plans" element={<SuperAdminPlans />} />
          <Route path="payments" element={<SuperAdminPayments />} />
          <Route path="subscriptions" element={<SuperAdminSubscriptionDashboard />} />
          <Route path="payment-management" element={<SuperAdminPaymentManagement />} />
          <Route path="commissions" element={<SuperAdminCommissions />} />
          <Route path="announcements" element={<SuperAdminAnnouncements />} />
          <Route path="activity-logs" element={<SuperAdminActivityLogs />} />
          <Route path="revenue" element={<SuperAdminDashboard />} />
          <Route path="store-health" element={<SuperAdminStoreHealth />} />
          <Route path="invoices" element={<SuperAdminInvoices />} />
          <Route path="tickets" element={<SuperAdminSupportTickets />} />
          <Route path="analytics" element={<SuperAdminAnalytics />} />
          <Route path="store-revenue" element={<SuperAdminStoreRevenue />} />
          <Route path="profile" element={<SuperAdminProfile />} />
          <Route path="security" element={<SuperAdminSecuritySettings />} />
          <Route path="terms-and-conditions" element={<SuperAdminTermsAndConditions />} />
          <Route path="privacy-policy" element={<SuperAdminPrivacyPolicy />} />
          <Route path="ads" element={<AdsManager />} />
          <Route path="ads/create" element={<CreateAd />} />
          <Route path="ads/:id/edit" element={<EditAd />} />
          <Route path="ads/:id/analytics" element={<AdAnalytics />} />
          <Route path="merchant-applications" element={<MerchantApplicationsPage />} />
        </Route>
        
        {/* Setup Wizard (standalone, no layout) */}
        <Route path="/admin/setup" element={<SetupWizard />} />
        
        {/* Store Admin Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="products/new" element={<ProductForm />} />
          <Route path="products/:id/edit" element={<ProductForm />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="coupons" element={<AdminCoupons />} />
          {/* <Route path="auto-reply" element={<AdminAutoReply />} /> */}
          <Route path="affiliates" element={<AdminAffiliates />} />
          <Route path="support" element={<AdminSupport />} />
          <Route path="subscription" element={<AdminSubscriptionPayment />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
        
        {/* Default redirect */}
        <Route path="/" element={<Landing />} />
      </Routes>
    </Suspense>
    </>
  );
}

export default App;
